import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Printer, Share2 } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import { OPTION_KEYS, TC_BLANK_LABELS } from '../../utils/Constants';
import MathText from '../ui/MathText';
import { ClickToViewImage } from '../ImageViewer';

const WEAK_THRESHOLD = 60;

// Option box colouring — mirrors UserQuizSubmissionDetail.getOptionStyle: green for a correct
// option (picked or not), red for a wrongly-picked one, plain otherwise.
const toLetterSet = (v) => new Set(String(v || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean));
function optionStyle(letter, q) {
    const isCorrect = toLetterSet(q.correct_option).has(letter);
    const isPicked = toLetterSet(q.selected_option).has(letter);
    if (isCorrect) return { backgroundColor: '#d1fae5', color: '#065f46', padding: '0.5rem', borderRadius: '0.25rem' };
    if (isPicked) return { backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.5rem', borderRadius: '0.25rem' };
    return { padding: '0.5rem', borderRadius: '0.25rem' };
}

// Weak subjects = those the user actually did badly in (accuracy below the threshold).
// Show at most the two worst. Ties on accuracy are broken by the subject with MORE
// questions in this quiz (more to practice); a remaining tie is broken randomly.
// If the user did well everywhere, return none — the caller shows a "practice
// random subjects" prompt instead of inventing a weak subject.
function pickWeakSubjects(bySubject) {
    if (!bySubject || bySubject.length === 0) return [];
    const questionCount = (s) => s.total ?? s.answered ?? 0;
    const below = bySubject.filter(s => s.accuracy_pct < WEAK_THRESHOLD);
    below.sort((a, b) => {
        if (a.accuracy_pct !== b.accuracy_pct) return a.accuracy_pct - b.accuracy_pct;
        if (questionCount(a) !== questionCount(b)) return questionCount(b) - questionCount(a);
        return Math.random() - 0.5;
    });
    return below.slice(0, 2);
}

function scoreBadgeColor(scorePct) {
    if (scorePct >= 80) return 'bg-success';
    if (scorePct >= 60) return 'bg-primary';
    if (scorePct >= 40) return 'bg-warning text-dark';
    return 'bg-danger';
}

function LandingReportCard({ result, level, onJumpToPractice, onRetake }) {
    const { userState } = useUser();
    const [shareMsg, setShareMsg] = useState('');
    const [showExplanation, setShowExplanation] = useState({}); // { question_id: bool }
    if (!result) return null;

    const { total = 0, correct = 0, score_pct = 0, by_subject = [], questions = [] } = result;
    const weakSubjects = pickWeakSubjects(by_subject);

    // Group reviewed questions by subject, mirroring the Quiz Submission Details layout.
    const questionsBySubject = {};
    questions.forEach(q => {
        const subj = q.subject || 'General';
        (questionsBySubject[subj] = questionsBySubject[subj] || []).push(q);
    });

    // One reviewed question card — styled like UserQuizSubmissionDetail (✓/✗, green/red option
    // boxes, LaTeX via MathText, collapsible explanation).
    const renderReviewQuestion = (q, no) => {
        const isTC = q.question_type === 'TEXT_COMPLETION';
        const isText = q.question_type === 'TEXT';
        const userSet = toLetterSet(q.selected_option);
        const answered = isTC || isText ? String(q.selected_option || q.text_answer || '').trim() !== '' : userSet.size > 0;
        return (
            <div key={q.question_id} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #eff6ff', borderRadius: '0.5rem', backgroundColor: '#f9fafb' }}>
                <p style={{ color: '#1f2937', margin: 0 }}>
                    <span style={{ color: q.is_correct ? '#16a34a' : '#dc2626', fontWeight: 800, fontSize: '1.3rem', marginRight: '0.4rem', verticalAlign: 'middle' }}>
                        {q.is_correct ? '✓' : '✗'}
                    </span>
                    {no ? <span style={{ fontWeight: 700, color: '#f97316', marginRight: 6 }}>Question {no}</span> : null}<MathText text={q.question_text} />
                </p>
                {q.question_image_url && (
                    <ClickToViewImage src={q.question_image_url} />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                    {isTC ? (() => {
                        const picks = String(q.selected_option || '').split(',').map(s => s.trim()).filter(s => s !== '').map(Number);
                        const blanks = (q.metadata && q.metadata.blanks) || [];
                        return blanks.map((blank, bi) => (
                            <div key={bi} className="mb-1">
                                <div className="fw-semibold text-secondary" style={{ fontSize: '0.85rem' }}>Blank {TC_BLANK_LABELS[bi] || `#${bi + 1}`}</div>
                                {(blank.choices || []).map((choice, ci) => {
                                    const style = { padding: '0.4rem 0.5rem', borderRadius: '0.25rem' };
                                    if (blank.correct === ci) { style.backgroundColor = '#d1fae5'; style.color = '#065f46'; }
                                    else if (picks[bi] === ci) { style.backgroundColor = '#fee2e2'; style.color = '#991b1b'; }
                                    return (
                                        <div key={ci} style={style} className="mb-1">
                                            <strong>{String.fromCharCode(65 + ci)}.</strong> <MathText text={choice} />
                                            {blank.correct === ci && <span className="badge bg-success ms-2">Correct</span>}
                                            {picks[bi] === ci && blank.correct !== ci && <span className="badge bg-danger ms-2">Your answer</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        ));
                    })() : isText ? (
                        <>
                            <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: q.correct_text_answer ? (q.is_correct ? '#d1fae5' : '#fee2e2') : '#eef2ff', color: q.correct_text_answer ? (q.is_correct ? '#065f46' : '#991b1b') : '#1f2937' }}>
                                <strong>Your Answer: </strong>{q.text_answer ? <MathText text={q.text_answer} /> : <span style={{ color: '#6b7280' }}>No answer</span>}
                            </div>
                            {q.correct_text_answer && (
                                <div style={{ padding: '0.5rem', borderRadius: '0.25rem', backgroundColor: '#d1fae5', color: '#065f46' }}><strong>Correct Answer: </strong><MathText text={q.correct_text_answer} /></div>
                            )}
                        </>
                    ) : (
                        OPTION_KEYS.map(fieldKey => {
                            const opt = fieldKey.slice(-1).toUpperCase();
                            if (!q[fieldKey]) return null;
                            const isCorrect = toLetterSet(q.correct_option).has(opt);
                            const isPicked = userSet.has(opt);
                            return (
                                <div key={opt} style={optionStyle(opt, q)}>
                                    <strong>{opt}. </strong><MathText text={q[fieldKey]} />
                                    {isCorrect && isPicked && <span className="badge bg-success ms-2">Your answer ✓</span>}
                                    {isCorrect && !isPicked && <span className="badge bg-success ms-2">Correct</span>}
                                    {isPicked && !isCorrect && <span className="badge bg-danger ms-2">Your answer</span>}
                                </div>
                            );
                        })
                    )}
                    {!answered && <div className="small text-muted">— skipped —</div>}
                    {(q.answer_explanation || q.explanation_image_url) && (
                        <div>
                            <button
                                onClick={() => setShowExplanation(prev => ({ ...prev, [q.question_id]: !prev[q.question_id] }))}
                                style={{ backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '0.375rem', padding: '0.3rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                            >
                                {showExplanation[q.question_id] ? 'Hide Explanation' : 'Show Explanation'}
                            </button>
                            {showExplanation[q.question_id] && (
                                <div style={{ marginTop: '0.5rem', padding: '0.6rem', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '0.375rem', fontSize: '0.85rem', color: '#374151', whiteSpace: 'pre-wrap' }}>
                                    {q.answer_explanation && <MathText text={q.answer_explanation} />}
                                    {q.explanation_image_url && (
                                        <ClickToViewImage src={q.explanation_image_url} containerStyle={{ marginTop: q.answer_explanation ? '0.5rem' : 0 }} />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleShare = async () => {
        const summary = `I scored ${correct}/${total} (${score_pct}%) on the Kobra Prep ${level || ''} test! Try it at ${window.location.origin}/`;
        try {
            await navigator.clipboard.writeText(summary);
            setShareMsg('Copied to clipboard!');
            setTimeout(() => setShareMsg(''), 2500);
        } catch {
            setShareMsg('Could not copy — please copy manually.');
            setTimeout(() => setShareMsg(''), 2500);
        }
    };

    const handlePrint = () => window.print();

    return (
        <div className="card shadow-sm report-card">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                    <h3 className="mb-0">Your report card</h3>
                    <div className="d-flex gap-2 no-print">
                        <button className="btn btn-outline-secondary btn-sm" onClick={handleShare}>
                            <Share2 size={14} className="me-1" />Share
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={handlePrint}>
                            <Printer size={14} className="me-1" />Print
                        </button>
                        {onRetake && (
                            <button className="btn btn-primary btn-sm" onClick={onRetake}>
                                Take another test
                            </button>
                        )}
                    </div>
                </div>
                {shareMsg && (
                    <div className="alert alert-info py-2 mb-3 no-print">{shareMsg}</div>
                )}

                <div className="text-center mb-4">
                    <span className={`badge ${scoreBadgeColor(score_pct)}`} style={{ fontSize: '1.5rem', padding: '0.5rem 1rem' }}>
                        {correct} / {total} — {score_pct}%
                    </span>
                </div>

                {/* Per-subject bars */}
                {by_subject.length > 0 && (
                    <div className="mb-4">
                        <h5>By subject</h5>
                        {by_subject.map(s => (
                            <div key={s.subject} className="mb-2">
                                <div className="d-flex justify-content-between small">
                                    <span>{s.subject}</span>
                                    <span>{s.correct}/{s.total ?? s.answered} ({s.accuracy_pct}%)</span>
                                </div>
                                <div className="progress" style={{ height: 8 }}>
                                    <div
                                        className={`progress-bar ${scoreBadgeColor(s.accuracy_pct)}`}
                                        role="progressbar"
                                        style={{ width: `${s.accuracy_pct}%` }}
                                        aria-valuenow={s.accuracy_pct}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Weak subjects callout — or, when nothing is weak, a prompt to
                  * keep practicing across all subjects. */}
                {onJumpToPractice && (
                    weakSubjects.length > 0 ? (
                        <div className="alert alert-warning no-print">
                            <strong>Want to improve?</strong> You scored lowest in:
                            <div className="mt-2 d-flex gap-2 flex-wrap">
                                {weakSubjects.map(s => (
                                    <button
                                        key={s.subject}
                                        className="btn btn-sm btn-outline-dark"
                                        onClick={() => onJumpToPractice(s.subject)}
                                    >
                                        Practice {s.subject} ({s.accuracy_pct}%) →
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-success no-print">
                            <strong>Great work — no weak subjects here!</strong> Keep it sharp by practicing random questions.
                            <div className="mt-2">
                                <button
                                    className="btn btn-sm btn-outline-dark"
                                    onClick={() => onJumpToPractice('')}
                                >
                                    Practice random questions →
                                </button>
                            </div>
                        </div>
                    )
                )}

                {/* Sign-up / log-in CTA — hidden when already logged in or printing */}
                {!userState?.isLoggedIn && (
                    <div className="alert alert-primary no-print">
                        <strong>Unlock the full app.</strong> Sign up to save favorites ⭐, track your daily streak 🔥,
                        access the full question catalog, see personal statistics, and compete on test leaderboards.
                        <div className="mt-2 d-flex gap-2">
                            <Link to="/signup" className="btn btn-sm btn-primary">Sign up</Link>
                            <Link to="/login" className="btn btn-sm btn-outline-primary">Log in</Link>
                        </div>
                    </div>
                )}

                {/* Per-question review — grouped by subject, styled like Quiz Submission Details */}
                <h5>Question review</h5>
                {(() => {
                    let n = 0;
                    return Object.entries(questionsBySubject).map(([subject, qs]) => (
                        <div key={subject} style={{ marginBottom: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
                            <h3 style={{ fontSize: '1.15rem', fontWeight: 'bold', color: '#1f2937', paddingBottom: '0.5rem', borderBottom: '1px solid #e5e7eb', marginBottom: '0.75rem' }}>
                                {subject} ({qs.length} question{qs.length !== 1 ? 's' : ''})
                            </h3>
                            {qs.map(q => renderReviewQuestion(q, ++n))}
                        </div>
                    ));
                })()}
            </div>
        </div>
    );
}

export default LandingReportCard;
